import classNames from 'classnames';
import React, { useMemo } from 'react';
import {
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from '@fluentui/react-components';

import { IMetricFormatter } from './types';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    height: '44px',
    justifyContent: 'center',
  },
  metricContainer: {
    display: 'flex',
    columnGap: '12px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
  },
  separator: {
    borderRightStyle: 'solid',
    borderRightWidth: '1px',
    borderRightColor: tokens.colorNeutralStroke1Selected,
  },
  box: {
    width: '12px',
    height: '12px',
    marginRight: '5px',
  },
  positiveBox: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
  },
  negativeBox: {
    backgroundColor: tokens.colorPaletteLilacForeground2,
  },
  changeContainer: {
    display: 'flex',
    alignItems: 'center',
  },
});

function formatDate(i18n: any, date: { start: Date, end: Date }) {
  const formattedStart = i18n.formatDate(date.start);
  const formattedEnd = i18n.formatDate(date.end);

  if (formattedStart === formattedEnd) {
    return formattedStart;
  }
  return `${formattedStart} - ${formattedEnd}`;
}

interface IPopoverInfoProps {
  i18n: any;
  change: number;
  percentageChange: string;
  metricName: string;
  localizedMetricName: string;
  currentMetric: string;
  periodMetric: string;
  entityName: string;
  currentDateRange: {
    end: Date;
    start: Date;
  };
  comparisonDateRange: {
    end: Date;
    start: Date;
  };
  metricFormatter: IMetricFormatter;
  currency: string;
}
export const PopoverInfo: React.FC<IPopoverInfoProps> = ({
  i18n, children, entityName, metricName, localizedMetricName,
  currentMetric, periodMetric, change, metricFormatter,
  percentageChange, currentDateRange, comparisonDateRange, currency,
}) => {
  const styles = useStyles();
  const formattedChange = useMemo(() => metricFormatter.formatMetricWithSign(metricName, change, { currency }), [metricFormatter, metricName, change]);
  const formattedPercentageChange = useMemo(() => metricFormatter.formatChangePercentageWithSign(percentageChange), [metricFormatter, percentageChange]);

  const formattedCurrentMetric = useMemo(() => metricFormatter.formatMetric(metricName, currentMetric, { currency }), [metricFormatter, metricName, currentMetric]);
  const formattedPeriodMetric = useMemo(() => metricFormatter.formatMetric(metricName, periodMetric, { currency }), [metricFormatter, metricName, periodMetric]);

  return (
    <Popover mouseLeaveDelay={0} openOnHover>
      <PopoverTrigger disableButtonEnhancement>
        <span
          className={classNames('trigger-wrapper', styles.trigger)}
          role="button"
          tabIndex={0}
          aria-expanded="true"
          aria-label={i18n.getString(_TL_('Expand change details'))}
        >
          {children}
        </span>
      </PopoverTrigger>

      <PopoverSurface tabIndex={-1}>
        <div>
          <div>{entityName}</div>
          <div className={styles.metricContainer}>
            <div className={styles.metric}>
              <div>{formatDate(i18n, currentDateRange)}</div>
              <div>{`${localizedMetricName}: ${formattedCurrentMetric}`}</div>
            </div>
            <div className={styles.separator} />
            <div className={styles.metric}>
              <div>{formatDate(i18n, comparisonDateRange)}</div>
              <div>{`${localizedMetricName}: ${formattedPeriodMetric}`}</div>
            </div>
          </div>
          <div className={styles.changeContainer}>
            {change !== 0 && <div className={classNames(styles.box, change > 0 ? styles.positiveBox : styles.negativeBox)} />}
            {i18n.getString(_TL_('Change: {{changeValue}} ({{changePercentage}})'), { changeValue: formattedChange, changePercentage: formattedPercentageChange })}
          </div>
        </div>
      </PopoverSurface>
    </Popover >
  );
};
